import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Phone, Mail, Facebook, Twitter, Linkedin, ArrowUp,
  Brain, TrendingUp, FileText, Package, StickyNote, Shield,
  Zap, BarChart3, ChevronRight, Sparkles, Bot, LineChart,
  PieChart, Star, Check, Sun, Moon,
} from 'lucide-react';
import { Link } from "react-router-dom";
const BASE_URL = process.env.REACT_APP_API_URL || "";

// ─── useScrollReveal hook ─────────────────────────────────────────────────────
function useScrollReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ─── RevealCard ───────────────────────────────────────────────────────────────
function RevealCard({ children, delay = 0, style = {}, className = "" }) {
  const [ref, visible] = useScrollReveal(0.1);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0) scale(1)" : "translateY(48px) scale(0.97)",
        transition: `opacity 0.7s ${delay}s cubic-bezier(.22,1,.36,1), transform 0.7s ${delay}s cubic-bezier(.22,1,.36,1)`,
      }}
    >
      {children}
    </div>
  );
}

// ─── AIHighlight ──────────────────────────────────────────────────────────────
function AIHighlight({ children, gMain, isDark }) {
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      {/* glowing underline bar */}
      <span style={{
        position: "absolute",
        bottom: "-3px",
        left: 0,
        right: 0,
        height: 3,
        borderRadius: 2,
        background: gMain,
        opacity: 0.85,
        filter: isDark ? "blur(1px)" : "none",
      }} />
      {/* animated glow ring on dark mode */}
      {isDark && (
        <span style={{
          position: "absolute",
          inset: "-6px -8px",
          borderRadius: 10,
          background: "radial-gradient(ellipse, rgba(0,229,255,0.13) 0%, transparent 70%)",
          pointerEvents: "none",
          animation: "aiGlow 3s ease-in-out infinite alternate",
        }} />
      )}
      <span style={{
        background: gMain,
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        fontWeight: 900,
        letterSpacing: "-0.5px",
      }}>
        {children}
      </span>
    </span>
  );
}

// ─── Animated Counter ─────────────────────────────────────────────────────────
function Counter({ end, suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        let start = 0;
        const step = end / (duration / 16);
        const timer = setInterval(() => {
          start += step;
          if (start >= end) { setCount(end); clearInterval(timer); }
          else setCount(Math.floor(start));
        }, 16);
      }
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{count}{suffix}</span>;
}

// ─── Particles ────────────────────────────────────────────────────────────────
function Particles({ isDark }) {
  const particles = useRef(
    Array.from({ length: 22 }, (_, i) => ({
      id: i, x: Math.random() * 100, y: Math.random() * 100,
      size: Math.random() * 5 + 2, delay: Math.random() * 6, duration: Math.random() * 8 + 6,
    }))
  ).current;
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
      {particles.map(p => (
        <div key={p.id} style={{
          position: "absolute", left: `${p.x}%`, top: `${p.y}%`,
          width: p.size, height: p.size, borderRadius: "50%",
          background: isDark ? "rgba(0,160,200,0.35)" : "rgba(0,100,150,0.18)",
          animation: `float ${p.duration}s ${p.delay}s infinite ease-in-out alternate`,
        }} />
      ))}
    </div>
  );
}

// ─── Theme Toggle ─────────────────────────────────────────────────────────────
function ThemeToggle({ isDark, toggle }) {
  return (
    <button onClick={toggle} aria-label="Toggle theme" style={{
      width: 52, height: 28, borderRadius: 14, border: "none", cursor: "pointer", padding: 0,
      background: isDark ? "linear-gradient(135deg,#00bcd4,#1de9b6)" : "linear-gradient(135deg,#f59e0b,#fb923c)",
      position: "relative", transition: "background 0.4s", flexShrink: 0,
    }}>
      <div style={{
        position: "absolute", top: 3, left: isDark ? 26 : 3,
        width: 22, height: 22, borderRadius: "50%",
        background: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
        transition: "left 0.35s cubic-bezier(.4,0,.2,1)", boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
      }}>
        {isDark ? <Moon size={12} color="#0ea5e9" strokeWidth={2.5} /> : <Sun size={12} color="#f59e0b" strokeWidth={2.5} />}
      </div>
    </button>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Landing1() {
  const [isDark, setIsDark] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ fullName: "", contact: "", email: "", message: "" });
  const [validationMessage, setValidationMessage] = useState({ fullName: "", contact: "", email: "", message: "" });
  const [formSuccess, setFormSuccess] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef(null);

  const D = isDark;
  const tk = D ? "d" : "l";

  const ac1 = D ? "#00e5ff" : "#006d8a";
  const ac2 = D ? "#1de9b6" : "#007a5e";
  const ac3 = D ? "#00bcd4" : "#0080a0";
  const ac4 = D ? "#80deea" : "#5ba3b8";

  const gMain = D ? "linear-gradient(120deg,#00e5ff,#00bcd4,#1de9b6)" : "linear-gradient(120deg,#005f80,#0080a0,#007a5e)";
  const gDiv  = D ? "linear-gradient(90deg,#00e5ff,#1de9b6)" : "linear-gradient(90deg,#006d8a,#007a5e)";
  const gBtn  = D ? "linear-gradient(135deg,#00bcd4,#00e5ff)" : "linear-gradient(135deg,#006d8a,#0097b5)";
  const gAvatar = D ? "linear-gradient(135deg,#00bcd4,#1de9b6)" : "linear-gradient(135deg,#006d8a,#007a5e)";
  const gLogo   = D ? "linear-gradient(135deg,#00bcd4,#1de9b6)" : "linear-gradient(135deg,#006d8a,#0097b5)";

  const gts = {
    background: gMain,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    display: "inline",
  };

  const t = {
    pageBg: D ? "#050d1a" : "#eef4fb",
    topbarBg: D ? "rgba(0,5,15,0.97)" : "rgba(255,255,255,0.97)",
    topbarBorder: D ? "rgba(0,200,255,0.1)" : "rgba(0,100,160,0.14)",
    navScrollBg: D ? "rgba(5,13,26,0.97)" : "rgba(255,255,255,0.97)",
    navScrollBorder: D ? "rgba(0,200,255,0.12)" : "rgba(0,100,160,0.16)",
    heroBg: D ? "radial-gradient(ellipse 80% 60% at 70% 40%,rgba(0,80,120,0.35) 0%,transparent 70%)"
      : "radial-gradient(ellipse 80% 60% at 70% 40%,rgba(0,90,140,0.08) 0%,transparent 70%)",
    gridColor: D ? "rgba(0,200,255,0.04)" : "rgba(0,100,160,0.06)",
    text: D ? "#e2eaf5" : "#0d1f38",
    textSub: D ? "rgba(160,190,220,0.82)" : "rgba(20,55,100,0.78)",
    textMuted: D ? "rgba(140,170,200,0.6)" : "rgba(40,80,130,0.6)",
    topbarLink: D ? "rgba(180,210,240,0.72)" : "rgba(10,55,110,0.72)",
    cardBg: D ? "rgba(0,18,38,0.72)" : "rgba(255,255,255,0.92)",
    cardBorder: D ? "rgba(0,200,255,0.12)" : "rgba(0,100,160,0.16)",
    glassBg: D ? "rgba(0,8,20,0.76)" : "rgba(255,255,255,0.88)",
    glassBorder: D ? "rgba(0,200,255,0.12)" : "rgba(0,100,160,0.18)",
    sectionBorder: D ? "rgba(0,200,255,0.08)" : "rgba(0,100,160,0.1)",
    socialBg: D ? "rgba(0,200,255,0.1)" : "rgba(0,100,160,0.07)",
    socialBorder: D ? "rgba(0,200,255,0.22)" : "rgba(0,100,160,0.18)",
    statBg: D ? "rgba(0,18,42,0.58)" : "rgba(255,255,255,0.88)",
    statBorder: D ? "rgba(0,200,255,0.12)" : "rgba(0,100,160,0.16)",
    serviceBg: D ? "rgba(0,18,40,0.58)" : "rgba(255,255,255,0.86)",
    serviceBorder: D ? "rgba(0,200,255,0.1)" : "rgba(0,100,160,0.14)",
    serviceHoverBg: D ? "rgba(0,30,60,0.85)" : "rgba(210,235,255,1)",
    iconBg: D ? "rgba(0,188,212,0.12)" : "rgba(0,109,138,0.1)",
    iconBorder: D ? "rgba(0,200,255,0.22)" : "rgba(0,109,138,0.24)",
    badgeBg: D ? "rgba(0,200,255,0.1)" : "rgba(0,109,138,0.08)",
    badgeBorder: D ? "rgba(0,200,255,0.3)" : "rgba(0,109,138,0.28)",
    dashBg: D ? "rgba(0,14,34,0.68)" : "rgba(220,238,255,0.95)",
    dashBorder: D ? "rgba(0,200,255,0.1)" : "rgba(0,100,150,0.18)",
    insightBg: D ? "rgba(0,229,255,0.07)" : "rgba(0,109,138,0.06)",
    insightBorder: D ? "rgba(0,229,255,0.16)" : "rgba(0,109,138,0.2)",
    testimonialBg: D ? "rgba(0,18,44,0.68)" : "rgba(255,255,255,0.9)",
    aboutCardBg: D ? "rgba(0,18,44,0.58)" : "rgba(255,255,255,0.86)",
    yearCardBg: D ? "rgba(0,18,44,0.68)" : "rgba(220,238,255,0.95)",
    barBg: D ? "rgba(0,200,255,0.1)" : "rgba(0,109,138,0.1)",
    footerBg: D ? "rgba(0,4,14,0.98)" : "rgba(230,242,255,1)",
    inputBg: D ? "rgba(0,18,45,0.88)" : "#f0f7ff",
    inputBorder: D ? "rgba(0,200,255,0.16)" : "rgba(0,100,160,0.22)",
    inputText: D ? "#e2eaf5" : "#0d1f38",
    liveBg: D ? "rgba(29,233,182,0.15)" : "rgba(0,122,94,0.12)",
    liveColor: D ? "#1de9b6" : "#006b50",
    starColor: D ? "#00e5ff" : "#006d8a",
    btnColor: D ? "#050d1a" : "#ffffff",
  };

  const slides = [
    { tag: "AI-Powered Platform", title: "Smart Expense Tracking with Real-Time AI Insights", desc: "Let AI categorize, predict, and optimize your expenses automatically — saving hours of manual work every week.", icon: <Brain size={64} />, color: ac1 },
    { tag: "Inventory Intelligence", title: "AI-Driven Quantity & Inventory Management", desc: "Predict stock shortfalls before they happen. Our AI engine recommends optimal reorder points and quantities.", icon: <Package size={64} />, color: ac1 },
    { tag: "Smart Documents", title: "Intelligent Document Management & Search", desc: "Upload once, find instantly. AI extracts, indexes, and surfaces the right document with semantic search.", icon: <FileText size={64} />, color: ac2 },
    { tag: "Note Intelligence", title: "AI Notes That Think Alongside You", desc: "Capture ideas in any format. AI organizes, links, and surfaces related notes automatically.", icon: <StickyNote size={64} />, color: ac1 },
  ];

  useEffect(() => {
    const timer = setInterval(() => setActiveSlide(s => (s + 1) % slides.length), 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrollY(y);
      setScrolled(y > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    setValidationMessage(p => ({ ...p, [name]: "" }));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.fullName.trim()) errs.fullName = "Full name is required.";
    if (!formData.email.trim()) errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = "Invalid email address.";
    if (!formData.contact.trim()) errs.contact = "Mobile number is required.";
    if (!formData.message.trim()) errs.message = "Message is required.";
    setValidationMessage(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSend = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/Users/ContactUs`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fullName: formData.fullName, contactNumber: formData.contact, email: formData.email, message: formData.message }),
      });
      setLoading(false);
      if (res.ok) {
        setFormSuccess(true);
        setFormData({ fullName: "", contact: "", email: "", message: "" });
        setTimeout(() => setFormSuccess(false), 5000);
      }
    } catch { setLoading(false); }
  };

  const features = [
    { icon: <Brain size={28} />, title: "AI Expense Categorization", desc: "Automatically classify thousands of transactions using ML trained on millions of financial records.", badge: "AI Core" },
    { icon: <LineChart size={28} />, title: "Predictive Spend Analytics", desc: "Forecast next-month expenses with 94% accuracy using historical patterns and seasonal models.", badge: "Forecasting" },
    { icon: <Package size={28} />, title: "Smart Inventory Alerts", desc: "AI monitors stock 24/7 and sends proactive alerts before you run out — with suggested reorder quantities.", badge: "Real-time" },
    { icon: <Bot size={28} />, title: "AI Document Parser", desc: "Extract data from invoices, receipts, and contracts automatically. No manual data entry ever again.", badge: "OCR + AI" },
    { icon: <PieChart size={28} />, title: "Unified Dashboard", desc: "A single command center for all your business data. Real-time charts, AI summaries, and recommendations.", badge: "Unified" },
    { icon: <Shield size={28} />, title: "Enterprise Security", desc: "Bank-grade encryption, role-based access control, and full audit trails protect sensitive data.", badge: "Security" },
  ];

  const services = [
    { icon: <Zap size={24} />, title: "Custom AI Software", desc: "Bespoke AI-powered software built around your exact business workflows and data." },
    { icon: <BarChart3 size={24} />, title: "Data Analytics & BI", desc: "Transform raw data into competitive intelligence with real-time dashboards and predictive models." },
    { icon: <Bot size={24} />, title: "AI Automation", desc: "Automate repetitive tasks with intelligent bots that learn and improve over time." },
    { icon: <Brain size={24} />, title: "ML Integration", desc: "Embed machine learning models directly into your existing systems and workflows." },
    { icon: <Shield size={24} />, title: "IT Consulting", desc: "Strategic tech advisory that aligns your digital investments with business growth." },
    { icon: <TrendingUp size={24} />, title: "Mobile & Web Apps", desc: "High-performance cross-platform applications designed for seamless user experiences." },
  ];

  const glassCard = { background: t.glassBg, backdropFilter: "blur(18px)", border: `1px solid ${t.glassBorder}`, borderRadius: 24 };
  const secBorder = { borderTop: `1px solid ${t.sectionBorder}` };
  const inputStyle = { background: t.inputBg, border: `1px solid ${t.inputBorder}`, borderRadius: 12, color: t.inputText, padding: "14px 18px", width: "100%", fontSize: 14, outline: "none", transition: "all .3s", fontFamily: "inherit" };

  // Parallax helpers
  const px = (speed) => `translateY(${scrollY * speed}px)`;

  return (
    <div style={{ fontFamily: "'Sora','DM Sans',sans-serif", background: t.pageBg, color: t.text, overflowX: "hidden", transition: "background 0.45s,color 0.45s" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

        @keyframes float        { from{transform:translateY(0)scale(1)}   to{transform:translateY(-22px)scale(1.1)} }
        @keyframes fadeSlideUp  { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin-slow    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes aiGlow       { from{opacity:.4} to{opacity:1} }
        @keyframes aiBlink      { 0%,100%{opacity:1} 50%{opacity:.5} }
        @keyframes orb1         { 0%,100%{transform:scale(1) translate(0,0)} 50%{transform:scale(1.08) translate(12px,-8px)} }
        @keyframes orb2         { 0%,100%{transform:scale(1) translate(0,0)} 50%{transform:scale(1.06) translate(-10px,10px)} }
        @keyframes shimmer      { 0%{background-position:200% center} 100%{background-position:-200% center} }

        * { box-sizing:border-box; margin:0; padding:0; }

        .btn-primary-ai{border:none;border-radius:50px;padding:14px 36px;font-weight:700;font-size:15px;cursor:pointer;transition:all .3s;font-family:inherit;}
        .btn-primary-ai:hover{transform:translateY(-2px);filter:brightness(1.1);}
        .btn-outline-ai{background:transparent;border-radius:50px;padding:13px 32px;font-weight:600;font-size:15px;cursor:pointer;transition:all .3s;font-family:inherit;}
        .btn-outline-ai:hover{transform:translateY(-2px);}
        .back-to-top-ai{position:fixed;bottom:32px;right:32px;width:48px;height:48px;border:none;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:transform .3s;z-index:999;}
        .back-to-top-ai:hover{transform:translateY(-4px);}
        .feature-card{border-radius:20px;padding:36px 28px;cursor:default;position:relative;overflow:hidden;transition:all .4s;}
        .service-card{border-radius:16px;padding:28px 24px;transition:all .3s;}

        /* AI word highlight shimmer */
        .ai-word {
          background: linear-gradient(90deg, #00e5ff, #1de9b6, #00bcd4, #00e5ff);
          background-size: 300% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 4s linear infinite;
          font-weight: 900;
        }
        .ai-word-light {
          background: linear-gradient(90deg, #005f80, #0080a0, #007a5e, #005f80);
          background-size: 300% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 4s linear infinite;
          font-weight: 900;
        }

        .parallax-layer { will-change: transform; }

        @media(max-width:768px){
          .hero-grid,.contact-grid{grid-template-columns:1fr!important;}
          .features-grid{grid-template-columns:1fr!important;}
          .services-grid{grid-template-columns:1fr 1fr!important;}
          .stats-grid{grid-template-columns:1fr 1fr!important;}
          .desktop-links{display:none!important;}
        }
        @media(max-width:480px){.services-grid{grid-template-columns:1fr!important;}}
      `}</style>

      {/* ═══ TOPBAR ═══════════════════════════════════════════════════════════ */}
      <div style={{ background: t.topbarBg, borderBottom: `1px solid ${t.topbarBorder}`, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 40px", flexWrap: "wrap", gap: 8, position: "relative", zIndex: 1001 }}>
        <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
          {[{ href: "tel:+919815340123", icon: <Phone size={13} color={ac1} />, label: "+91 9815-34-0123" },
          { href: "mailto:info@antheminfotech.com", icon: <Mail size={13} color={ac1} />, label: "info@antheminfotech.com" }].map(({ href, icon, label }) => (
            <a key={label} href={href} style={{ color: t.topbarLink, textDecoration: "none", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>{icon}{label}</a>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {[
            { href: "https://www.facebook.com/AnthemInfotech", icon: <Facebook size={15} /> },
            { href: "https://twitter.com/antheminfotech", icon: <Twitter size={15} /> },
            { href: "https://www.linkedin.com/company/anthem-infotech-pvt-ltd-", icon: <Linkedin size={15} /> },
            {
              href: "https://api.whatsapp.com/send/?phone=919815340123",
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.52 3.48A11.83 11.83 0 0 0 12.01 0C5.37 0 .02 5.35.02 11.99c0 2.11.55 4.17 1.6 5.99L0 24l6.18-1.61a11.94 11.94 0 0 0 5.83 1.48h.01c6.64 0 12-5.35 12-11.99 0-3.2-1.25-6.2-3.5-8.4zM12 21.5c-1.8 0-3.56-.48-5.1-1.38l-.36-.21-3.67.96.98-3.58-.23-.37a9.46 9.46 0 0 1-1.45-5.03c0-5.25 4.27-9.52 9.53-9.52 2.54 0 4.93.99 6.72 2.78a9.43 9.43 0 0 1 2.8 6.72c0 5.25-4.28 9.53-9.53 9.53zm5.22-7.15c-.28-.14-1.66-.82-1.92-.91-.26-.1-.45-.14-.64.14-.19.28-.73.91-.9 1.1-.17.19-.33.21-.61.07-.28-.14-1.17-.43-2.23-1.38-.82-.73-1.37-1.63-1.53-1.9-.16-.28-.02-.43.12-.57.13-.13.28-.33.42-.5.14-.17.19-.28.28-.47.09-.19.05-.36-.02-.5-.07-.14-.64-1.54-.88-2.11-.23-.56-.47-.48-.64-.49h-.55c-.19 0-.5.07-.76.36s-1 1-1 2.43c0 1.43 1.03 2.81 1.17 3 .14.19 2.02 3.09 4.9 4.33.69.3 1.23.48 1.65.62.69.22 1.32.19 1.82.12.55-.08 1.66-.68 1.9-1.34.23-.66.23-1.22.16-1.34-.07-.12-.26-.19-.54-.33z" />
                </svg>
              )
            }
          ].map(({ href, icon }) => (
            <a key={href} href={href} target="_blank" rel="noopener noreferrer"
              style={{ width: 30, height: 30, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: t.socialBg, color: ac1, border: `1px solid ${t.socialBorder}`, textDecoration: "none" }}>
              {icon}
            </a>
          ))}
        </div>
      </div>

      {/* ═══ NAVBAR ═══════════════════════════════════════════════════════════ */}
      <nav style={{ position: "sticky", top: 0, zIndex: 1000, padding: scrolled ? "10px 40px" : "16px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "all .4s", background: scrolled ? t.navScrollBg : "transparent", backdropFilter: scrolled ? "blur(20px)" : "none", borderBottom: scrolled ? `1px solid ${t.navScrollBorder}` : "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: gLogo, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Brain size={22} color={t.btnColor} />
          </div>
          <div>
            <div key={tk + "-nav"} style={{ fontWeight: 800, fontSize: 22, ...gts }}>Essentials</div>
            <div style={{ fontSize: 12, color: t.textMuted, letterSpacing: 2, textTransform: "uppercase", marginTop: -2 }}>
              <span className={D ? "ai-word" : "ai-word-light"} style={{ fontSize: 12, letterSpacing: 2 }}>AI</span> Platform
            </div>
          </div>
        </div>
        <div className="desktop-links" style={{ display: "flex", gap: 4, alignItems: "center" }}>
          {["Features", "Services", "About", "Contact"].map(item => (
            <a key={item} href={`#${item.toLowerCase()}`} style={{ textDecoration: "none", fontSize: 14, fontWeight: 500, padding: "8px 16px", borderRadius: 8, color: t.text, transition: "all .2s" }}
              onMouseEnter={e => { e.currentTarget.style.color = ac1; e.currentTarget.style.background = D ? "rgba(0,200,255,0.08)" : "rgba(0,100,160,0.07)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = t.text; e.currentTarget.style.background = "transparent"; }}>{item}</a>
          ))}
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <ThemeToggle isDark={isDark} toggle={() => setIsDark(d => !d)} />
          <Link to="/login">
            <button className="btn-primary-ai" style={{ background: gBtn, color: t.btnColor }}>Login</button>
          </Link>
        </div>
      </nav>

      {/* ═══ HERO — deep multi-layer parallax ═══════════════════════════════ */}
      <section
        ref={heroRef}
        style={{ minHeight: "90vh", display: "flex", alignItems: "center", position: "relative", overflow: "hidden", padding: "60px 40px" }}
      >
        {/* Layer 0: deep background gradient — barely moves (0.08x) */}
        <div className="parallax-layer" style={{
          position: "absolute", inset: 0,
          background: t.heroBg,
          transform: px(0.08),
        }} />

        {/* Layer 1: grid — slow (0.18x) */}
        <div className="parallax-layer" style={{
          position: "absolute", inset: 0,
          backgroundImage: `linear-gradient(${t.gridColor} 1px,transparent 1px),linear-gradient(90deg,${t.gridColor} 1px,transparent 1px)`,
          backgroundSize: "60px 60px",
          transform: px(0.18),
        }} />

        {/* Layer 2: large orb top-right — very slow (0.07x) */}
        <div className="parallax-layer" style={{
          position: "absolute", top: "-12%", right: "-6%",
          width: "48vw", height: "48vw", borderRadius: "50%",
          background: D
            ? "radial-gradient(circle, rgba(0,200,255,0.10) 0%, transparent 65%)"
            : "radial-gradient(circle, rgba(0,130,180,0.08) 0%, transparent 65%)",
          pointerEvents: "none",
          animation: "orb1 14s ease-in-out infinite",
          transform: px(0.07),
        }} />

        {/* Layer 3: secondary orb bottom-left (0.14x) */}
        <div className="parallax-layer" style={{
          position: "absolute", bottom: "-18%", left: "-6%",
          width: "38vw", height: "38vw", borderRadius: "50%",
          background: D
            ? "radial-gradient(circle, rgba(29,233,182,0.07) 0%, transparent 65%)"
            : "radial-gradient(circle, rgba(0,122,94,0.06) 0%, transparent 65%)",
          pointerEvents: "none",
          animation: "orb2 18s ease-in-out infinite",
          transform: px(0.14),
        }} />

        {/* Layer 4: mid accent orb centre-left (0.22x) */}
        <div className="parallax-layer" style={{
          position: "absolute", top: "30%", left: "20%",
          width: "22vw", height: "22vw", borderRadius: "50%",
          background: D
            ? "radial-gradient(circle, rgba(0,150,200,0.07) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(0,80,130,0.04) 0%, transparent 70%)",
          pointerEvents: "none",
          transform: px(0.22),
        }} />

        {/* Layer 5: particles — fastest (0.32x) */}
        <div className="parallax-layer" style={{
          position: "absolute", inset: 0,
          transform: px(0.32),
        }}>
          <Particles isDark={isDark} />
        </div>

        {/* ── Hero content (z-index above all layers, no parallax) */}
        <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "center", position: "relative", zIndex: 2 }} className="hero-grid">
          <div style={{ animation: "fadeSlideUp .9s ease forwards" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: t.badgeBg, border: `1px solid ${t.badgeBorder}`, color: ac1, borderRadius: 100, padding: "5px 14px", fontSize: 12, fontWeight: 600, marginBottom: 24 }}>
              <Sparkles size={12} /> <span className={D ? "ai-word" : "ai-word-light"} style={{ fontSize: 12 }}>AI</span>-Powered Business Suite
            </div>
            <h1 style={{ fontSize: "clamp(36px,5vw,62px)", fontWeight: 800, lineHeight: 1.1, marginBottom: 24 }}>
              The Smartest Way to<br />
              <span key={tk + "-h1"} style={{ ...gts }}>Track, Manage &<br />Grow</span> Your Business
            </h1>
            <p style={{ fontSize: 17, lineHeight: 1.7, color: t.textSub, marginBottom: 36, maxWidth: 520 }}>
              <span className={D ? "ai-word" : "ai-word-light"}>AI</span>-driven expense tracking, inventory intelligence, smart documents, and unified analytics — all in one platform that learns your business and gets smarter every day.
            </p>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <button className="btn-primary-ai" style={{ background: gBtn, color: t.btnColor }}>
                Start Free Trial <ChevronRight size={16} style={{ display: "inline", marginLeft: 4 }} />
              </button>
              <button className="btn-outline-ai" style={{ color: ac1, border: `1.5px solid ${ac1}` }}
                onMouseEnter={e => { e.currentTarget.style.background = D ? "rgba(0,229,255,0.1)" : "rgba(0,109,138,0.08)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>Watch Demo</button>
            </div>
            <div style={{ display: "flex", gap: 28, marginTop: 40, flexWrap: "wrap" }}>
              {[["500+", "Companies"], ["4.9★", "Rating"], ["99.9%", "Uptime"]].map(([num, label]) => (
                <div key={label}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: ac1 }}>{num}</div>
                  <div style={{ fontSize: 12, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", inset: -20, background: D ? "radial-gradient(ellipse,rgba(0,180,220,0.1) 0%,transparent 70%)" : "radial-gradient(ellipse,rgba(0,100,150,0.06) 0%,transparent 70%)", borderRadius: "50%" }} />
            <div style={{ ...glassCard, padding: 36, animation: "fadeSlideUp 1.1s .2s ease both" }}>
              <div style={{ width: 80, height: 80, borderRadius: 20, background: t.iconBg, border: `1.5px solid ${t.iconBorder}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20, color: slides[activeSlide].color, transition: "all .5s" }}>
                {slides[activeSlide].icon}
              </div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: t.badgeBg, border: `1px solid ${t.badgeBorder}`, color: ac1, borderRadius: 100, padding: "4px 12px", fontSize: 11, fontWeight: 600, marginBottom: 14 }}>
                {slides[activeSlide].tag}
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, lineHeight: 1.3, color: t.text }}>{slides[activeSlide].title}</h3>
              <p style={{ fontSize: 14, color: t.textSub, lineHeight: 1.7, marginBottom: 20 }}>{slides[activeSlide].desc}</p>
              <div style={{ background: t.dashBg, borderRadius: 12, padding: "14px 16px", border: `1px solid ${t.dashBorder}` }}>
                <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 10, display: "flex", justifyContent: "space-between" }}>
                  <span><span className={D ? "ai-word" : "ai-word-light"} style={{ fontSize: 11 }}>AI</span> Analysis</span><span style={{ color: ac2 }}>Live ●</span>
                </div>
                <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 48 }}>
                  {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 100].map((h, i) => (
                    <div key={i} style={{
                      flex: 1, height: `${h}%`, borderRadius: 3,
                      background: D ? `linear-gradient(180deg,#00e5ff ${h > 70 ? "0%" : "60%"},rgba(0,150,200,0.3))`
                        : `linear-gradient(180deg,${ac1} ${h > 70 ? "0%" : "60%"},rgba(0,100,150,0.2))`,
                      opacity: i < activeSlide % 12 + 5 ? 1 : 0.3, transition: "height .5s,opacity .5s"
                    }} />
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 20, justifyContent: "center" }}>
                {slides.map((_, i) => (
                  <button key={i} onClick={() => setActiveSlide(i)} style={{
                    width: i === activeSlide ? 24 : 8, height: 8, borderRadius: 4, border: "none", cursor: "pointer", transition: "all .3s",
                    background: i === activeSlide ? ac1 : D ? "rgba(0,229,255,.28)" : "rgba(0,109,138,0.25)",
                  }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ STATS — scroll-reveal ═════════════════════════════════════════════ */}
      <section style={{ padding: "60px 40px", ...secBorder, borderBottom: `1px solid ${t.sectionBorder}` }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 20 }} className="stats-grid">
          {[{ value: 500, suffix: "+", label: "Businesses Onboarded" }, { value: 98, suffix: "%", label: "Customer Satisfaction" }, { value: 40, suffix: "%", label: "Avg. Cost Reduction" }, { value: 25, suffix: "yrs", label: "Industry Experience" }].map(({ value, suffix, label }, i) => (
            <RevealCard key={label} delay={i * 0.1} style={{ background: t.statBg, border: `1px solid ${t.statBorder}`, borderRadius: 16, padding: "32px 24px", textAlign: "center", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: "60%", height: 2, background: gDiv, borderRadius: 1 }} />
              <div style={{ fontSize: 42, fontWeight: 800, color: ac1, lineHeight: 1, marginTop: 4 }}>
                <Counter end={value} suffix={suffix} />
              </div>
              <div style={{ fontSize: 13, color: t.textMuted, marginTop: 8 }}>{label}</div>
            </RevealCard>
          ))}
        </div>
      </section>

      {/* ═══ FEATURES — staggered scroll-reveal ══════════════════════════════ */}
      <section id="features" style={{ padding: "100px 40px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <RevealCard style={{ textAlign: "center", marginBottom: 64 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: ac1, marginBottom: 12 }}>Platform Features</div>
            <h2 style={{ fontSize: "clamp(28px,4vw,44px)", fontWeight: 800, marginBottom: 16 }}>
              <span className={D ? "ai-word" : "ai-word-light"}>AI</span> at the Core of<br /><span key={tk + "-fh"} style={{ ...gts }}>Everything You Do</span>
            </h2>
            <div style={{ width: 60, height: 3, background: gDiv, borderRadius: 2, margin: "16px auto 0" }} />
          </RevealCard>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24 }} className="features-grid">
            {features.map(({ icon, title, desc, badge }, i) => (
              <RevealCard key={title} delay={i * 0.08}>
                <div className="feature-card" style={{ background: t.cardBg, border: `1px solid ${t.cardBorder}`, height: "100%" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-6px)"; e.currentTarget.style.boxShadow = D ? "0 20px 60px rgba(0,180,220,0.2)" : "0 20px 60px rgba(0,100,150,0.12)"; e.currentTarget.style.borderColor = ac1; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = t.cardBorder; }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                    <div style={{ width: 52, height: 52, borderRadius: 14, background: t.iconBg, border: `1px solid ${t.iconBorder}`, display: "flex", alignItems: "center", justifyContent: "center", color: ac1 }}>{icon}</div>
                    <span style={{ background: t.badgeBg, border: `1px solid ${t.badgeBorder}`, borderRadius: 100, padding: "3px 10px", fontSize: 10, color: ac1, fontWeight: 700, letterSpacing: .5 }}>{badge}</span>
                  </div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 10, color: t.text }}>{title}</h3>
                  <p style={{ fontSize: 14, color: t.textSub, lineHeight: 1.65 }}>{desc}</p>
                </div>
              </RevealCard>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ AI SHOWCASE — split parallax ════════════════════════════════════ */}
      <section style={{ padding: "100px 40px", position: "relative", overflow: "hidden", ...secBorder }}>
        {/* Background layer with its own parallax */}
        <div className="parallax-layer" style={{
          position: "absolute", inset: 0,
          background: D ? "radial-gradient(ellipse 90% 60% at 20% 50%,rgba(0,60,100,0.4) 0%,transparent 70%)" : "radial-gradient(ellipse 90% 60% at 20% 50%,rgba(0,80,130,0.05) 0%,transparent 70%)",
          transform: px(0.12),
        }} />
        {/* Decorative floating ring */}
        <div className="parallax-layer" style={{
          position: "absolute", top: "10%", right: "5%",
          width: 280, height: 280,
          borderRadius: "50%",
          border: `1px solid ${D ? "rgba(0,200,255,0.08)" : "rgba(0,100,160,0.07)"}`,
          transform: px(0.2),
          pointerEvents: "none",
        }} />
        <div className="parallax-layer" style={{
          position: "absolute", bottom: "5%", left: "8%",
          width: 180, height: 180,
          borderRadius: "50%",
          border: `1px solid ${D ? "rgba(29,233,182,0.08)" : "rgba(0,122,94,0.06)"}`,
          transform: px(0.28),
          pointerEvents: "none",
        }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }} className="hero-grid">
          <RevealCard delay={0}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: ac1, marginBottom: 12 }}>
              <span className={D ? "ai-word" : "ai-word-light"}>AI</span> Intelligence
            </div>
            <h2 style={{ fontSize: "clamp(28px,4vw,42px)", fontWeight: 800, lineHeight: 1.2, marginBottom: 20 }}>
              Your Finances,<br /><span key={tk + "-ah"} style={{ ...gts }}>Understood by <span className={D ? "ai-word" : "ai-word-light"}>AI</span></span>
            </h2>
            <div style={{ width: 60, height: 3, background: gDiv, borderRadius: 2, margin: "0 0 28px" }} />
            <p style={{ color: t.textSub, lineHeight: 1.75, marginBottom: 32, fontSize: 15 }}>
              Our <span className={D ? "ai-word" : "ai-word-light"}>AI</span> expense engine doesn't just record transactions — it understands them. It spots anomalies, predicts trends, flags tax-deductible items, and generates actionable reports in seconds.
            </p>
            {["Automatic receipt scanning & categorization", "Multi-currency with live exchange rates", "AI anomaly detection for fraud prevention", "One-click tax-ready expense reports", "Team expense policies with auto-approval"].map((item, i) => (
              <RevealCard key={item} delay={0.05 * i}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: gAvatar, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Check size={12} color={t.btnColor} strokeWidth={3} />
                  </div>
                  <span style={{ fontSize: 14, color: t.textSub }}>{item}</span>
                </div>
              </RevealCard>
            ))}
            <button className="btn-primary-ai" style={{ marginTop: 32, background: gBtn, color: t.btnColor }}>Explore <span style={{ background: gMain, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>AI</span> Features</button>
          </RevealCard>

          <RevealCard delay={0.15}>
            <div style={{ ...glassCard, padding: 28 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: t.text }}><span className={D ? "ai-word" : "ai-word-light"}>AI</span> Expense Dashboard</span>
                <span style={{ background: t.liveBg, color: t.liveColor, borderRadius: 100, padding: "4px 12px", fontSize: 11, fontWeight: 700 }}>● Live</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
                {[["₹2.4L", "Total Spend", ac1], ["₹38K", "AI Saved", ac2], ["94%", "Accuracy", ac3]].map(([val, label, color]) => (
                  <div key={label} style={{ background: t.dashBg, borderRadius: 12, padding: "14px 12px", border: `1px solid ${t.dashBorder}`, textAlign: "center" }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color }}>{val}</div>
                    <div style={{ fontSize: 10, color: t.textMuted, marginTop: 3 }}>{label}</div>
                  </div>
                ))}
              </div>
              {[["Food & Travel", 65, ac1], ["Operations", 80, ac2], ["Marketing", 45, ac3], ["Utilities", 30, ac4]].map(([name, pct, color]) => (
                <div key={name} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
                    <span style={{ color: t.textSub }}>{name}</span>
                    <span style={{ color, fontWeight: 700 }}>{pct}%</span>
                  </div>
                  <div style={{ height: 6, background: t.barBg, borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: `linear-gradient(90deg,${color},${color}80)`, borderRadius: 3, transition: "width .6s ease" }} />
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 16, padding: "14px 16px", background: t.insightBg, borderRadius: 12, border: `1px solid ${t.insightBorder}`, display: "flex", alignItems: "center", gap: 10 }}>
                <Brain size={18} color={ac1} />
                <span style={{ fontSize: 12, color: t.textSub }}><span className={D ? "ai-word" : "ai-word-light"} style={{ fontSize: 12 }}>AI</span> Insight: Marketing spend up 12% MoM. Review budget allocation.</span>
              </div>
            </div>
          </RevealCard>
        </div>
      </section>

      {/* ═══ SERVICES — staggered reveal ═════════════════════════════════════ */}
      <section id="services" style={{ padding: "100px 40px", ...secBorder }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <RevealCard style={{ textAlign: "center", marginBottom: 60 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: ac1, marginBottom: 12 }}>What We Build</div>
            <h2 style={{ fontSize: "clamp(26px,4vw,42px)", fontWeight: 800, marginBottom: 12 }}>
              <span key={tk + "-sh"} style={{ ...gts }}>Services</span> That Drive Growth
            </h2>
            <div style={{ width: 60, height: 3, background: gDiv, borderRadius: 2, margin: "16px auto 0" }} />
          </RevealCard>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }} className="services-grid">
            {services.map(({ icon, title, desc }, i) => (
              <RevealCard key={title} delay={i * 0.07}>
                <div className="service-card" style={{ background: t.serviceBg, border: `1px solid ${t.serviceBorder}`, height: "100%" }}
                  onMouseEnter={e => { e.currentTarget.style.background = t.serviceHoverBg; e.currentTarget.style.borderColor = D ? "rgba(0,200,255,0.32)" : "rgba(0,100,160,0.3)"; e.currentTarget.style.transform = "translateY(-4px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = t.serviceBg; e.currentTarget.style.borderColor = t.serviceBorder; e.currentTarget.style.transform = "translateY(0)"; }}>
                  <div style={{ width: 46, height: 46, borderRadius: 12, background: t.iconBg, border: `1px solid ${t.iconBorder}`, display: "flex", alignItems: "center", justifyContent: "center", color: ac1, marginBottom: 16 }}>{icon}</div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: t.text }}>{title}</h3>
                  <p style={{ fontSize: 13, color: t.textSub, lineHeight: 1.6 }}>{desc}</p>
                </div>
              </RevealCard>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ABOUT — parallax background + reveal ════════════════════════════ */}
      <section id="about" style={{ padding: "100px 40px", ...secBorder, position: "relative", overflow: "hidden" }}>
        {/* background orb with parallax */}
        <div className="parallax-layer" style={{
          position: "absolute", top: "0%", right: "-10%",
          width: "50vw", height: "50vw", borderRadius: "50%",
          background: D
            ? "radial-gradient(circle, rgba(0,200,255,0.05) 0%, transparent 65%)"
            : "radial-gradient(circle, rgba(0,130,180,0.04) 0%, transparent 65%)",
          pointerEvents: "none",
          transform: px(0.16),
        }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center", position: "relative", zIndex: 2 }} className="hero-grid">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[{ icon: <Brain size={32} />, title: "AI First", sub: "Built for the AI era", color: ac1 },
            { icon: <Shield size={32} />, title: "Secure", sub: "Bank-grade encryption", color: ac2 },
            { icon: <Zap size={32} />, title: "Fast", sub: "Sub-second responses", color: ac3 },
            { icon: <TrendingUp size={32} />, title: "Scalable", sub: "Grows with you", color: ac4 }].map(({ icon, title, sub, color }, i) => (
              <RevealCard key={title} delay={i * 0.1}>
                <div style={{ background: t.aboutCardBg, border: `1px solid ${t.cardBorder}`, borderRadius: 20, padding: "28px 20px", textAlign: "center", transition: "all .3s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.borderColor = color; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = t.cardBorder; }}>
                  <div style={{ color, marginBottom: 12 }}>{icon}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: t.text }}>{title}</div>
                  <div style={{ fontSize: 12, color: t.textMuted }}>{sub}</div>
                </div>
              </RevealCard>
            ))}
          </div>
          <RevealCard delay={0.2}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: ac1, marginBottom: 12 }}>About Anthem Infotech</div>
            <h2 style={{ fontSize: "clamp(26px,4vw,40px)", fontWeight: 800, lineHeight: 1.2, marginBottom: 16 }}>
              25 Years of Innovation,<br /><span key={tk + "-abh"} style={{ ...gts }}>Now <span className={D ? "ai-word" : "ai-word-light"}>AI</span>-Powered</span>
            </h2>
            <div style={{ width: 60, height: 3, background: gDiv, borderRadius: 2, margin: "0 0 28px" }} />
            <p style={{ color: t.textSub, lineHeight: 1.75, marginBottom: 16, fontSize: 15 }}>
              Founded in Ludhiana, Anthem Infotech has spent 25 years building software businesses trust. Today, we bring the power of artificial intelligence to every product we build — making your data work harder so you don't have to.
            </p>
            <p style={{ color: t.textSub, lineHeight: 1.75, marginBottom: 32, fontSize: 15 }}>
              From custom <span className={D ? "ai-word" : "ai-word-light"}>AI</span> solutions to enterprise platforms, we combine deep domain expertise with cutting-edge technology to deliver products that truly transform how businesses operate.
            </p>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {[["25", "Years Experience", ac1], ["500+", "Projects Delivered", ac2]].map(([val, label, color]) => (
                <div key={label} style={{ padding: "16px 24px", background: t.yearCardBg, border: `1px solid ${t.cardBorder}`, borderRadius: 14, textAlign: "center" }}>
                  <div style={{ fontSize: 32, fontWeight: 800, color }}>{val}</div>
                  <div style={{ fontSize: 12, color: t.textMuted }}>{label}</div>
                </div>
              ))}
            </div>
          </RevealCard>
        </div>
      </section>

      {/* ═══ CONTACT ═══════════════════════════════════════════════════════════ */}
      <section id="contact" style={{ padding: "100px 40px", ...secBorder }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80 }} className="contact-grid">
          <RevealCard delay={0}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: ac1, marginBottom: 12 }}>Get in Touch</div>
            <h2 style={{ fontSize: "clamp(26px,4vw,40px)", fontWeight: 800, lineHeight: 1.2, marginBottom: 16 }}>
              Start Your<br /><span key={tk + "-ch"} style={{ ...gts }}><span className={D ? "ai-word" : "ai-word-light"}>AI</span> Journey Today</span>
            </h2>
            <div style={{ width: 60, height: 3, background: gDiv, borderRadius: 2, margin: "0 0 28px" }} />
            <p style={{ color: t.textSub, lineHeight: 1.75, marginBottom: 36, fontSize: 15 }}>
              Schedule a consultation with our <span className={D ? "ai-word" : "ai-word-light"}>AI</span> experts. We'll analyze your business workflows and show you exactly how our platform can save you time and money.
            </p>
            {[{ icon: <Phone size={22} />, label: "Call Us", value: "+91 9815-34-0123", href: "tel:+919815340123" },
            { icon: <Mail size={22} />, label: "Email Us", value: "info@antheminfotech.com", href: "mailto:info@antheminfotech.com" }].map(({ icon, label, value, href }) => (
              <a key={label} href={href} style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, textDecoration: "none" }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: t.iconBg, border: `1px solid ${t.iconBorder}`, display: "flex", alignItems: "center", justifyContent: "center", color: ac1, flexShrink: 0 }}>{icon}</div>
                <div>
                  <div style={{ fontSize: 12, color: t.textMuted }}>{label}</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: t.text }}>{value}</div>
                </div>
              </a>
            ))}
          </RevealCard>

          <RevealCard delay={0.15}>
            <div style={{ ...glassCard, padding: 36 }}>
              {formSuccess ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <div style={{ width: 64, height: 64, borderRadius: "50%", background: gAvatar, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                    <Check size={28} color={t.btnColor} strokeWidth={3} />
                  </div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 10, color: t.text }}>Message Sent!</h3>
                  <p style={{ color: t.textSub, fontSize: 14 }}>Our team will get back to you within 24 hours.</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: t.text }}>Send a Message</h3>
                  <div>
                    <input style={inputStyle} name="fullName" value={formData.fullName} onChange={handleChange} placeholder="Your Full Name" maxLength={30} />
                    {validationMessage.fullName && <div style={{ color: "#c0392b", fontSize: 11, marginTop: 5 }}>{validationMessage.fullName}</div>}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <input style={inputStyle} name="email" value={formData.email} onChange={handleChange} placeholder="Email Address" maxLength={50} />
                      {validationMessage.email && <div style={{ color: "#c0392b", fontSize: 11, marginTop: 5 }}>{validationMessage.email}</div>}
                    </div>
                    <div>
                      <input style={inputStyle} name="contact" value={formData.contact} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ""); setFormData(p => ({ ...p, contact: v })); setValidationMessage(p => ({ ...p, contact: "" })); }} placeholder="Mobile Number" maxLength={15} />
                      {validationMessage.contact && <div style={{ color: "#c0392b", fontSize: 11, marginTop: 5 }}>{validationMessage.contact}</div>}
                    </div>
                  </div>
                  <div>
                    <textarea style={{ ...inputStyle, resize: "none" }} name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your project..." rows={5} maxLength={300} />
                    {validationMessage.message && <div style={{ color: "#c0392b", fontSize: 11, marginTop: 5 }}>{validationMessage.message}</div>}
                  </div>
                  <button className="btn-primary-ai" onClick={handleSend} disabled={loading} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: gBtn, color: t.btnColor }}>
                    {loading
                      ? <><div style={{ width: 18, height: 18, border: `2px solid ${D ? "rgba(5,13,26,.3)" : "rgba(255,255,255,.3)"}`, borderTop: `2px solid ${t.btnColor}`, borderRadius: "50%", animation: "spin-slow .7s linear infinite" }} /> Sending...</>
                      : <><Sparkles size={16} /> Send Message</>
                    }
                  </button>
                </div>
              )}
            </div>
          </RevealCard>
        </div>
      </section>

      {/* ═══ FOOTER ════════════════════════════════════════════════════════════ */}
      <footer style={{ background: t.footerBg, borderTop: `1px solid ${t.sectionBorder}`, padding: "32px 40px", transition: "background .45s" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div key={tk + "-footer"} style={{ fontWeight: 800, fontSize: 16, ...gts, marginBottom: 4 }}>Essentials <span className={D ? "ai-word" : "ai-word-light"} style={{ fontSize: 16 }}>AI</span> Platform</div>
            <div style={{ fontSize: 12, color: t.textMuted }}>© {new Date().getFullYear()} Anthem Infotech Pvt. Ltd. All rights reserved.</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <ThemeToggle isDark={isDark} toggle={() => setIsDark(d => !d)} />
            <span style={{ fontSize: 12, color: t.textMuted }}>
              Powered by{" "}
              <a href="https://antheminfotech.com/" target="_blank" rel="noopener noreferrer" style={{ color: ac1, textDecoration: "none", fontWeight: 600 }}>
                Anthem Infotech Pvt. Ltd.
              </a>
            </span>
          </div>
        </div>
      </footer>

      {/* ═══ BACK TO TOP ═══════════════════════════════════════════════════════ */}
      <button className="back-to-top-ai" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        style={{ background: gBtn, color: t.btnColor, boxShadow: D ? "0 8px 24px rgba(0,200,255,.35)" : "0 8px 24px rgba(0,100,150,.25)" }}>
        <ArrowUp size={20} />
      </button>
    </div>
  );
}